import {
  Brain,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  ScanSearch,
  Sparkles,
  Zap,
} from "lucide-react";
import { useState } from "react";
import * as S from "../dashboard/_shared";
import Tag from "../tag";
import "./style.scss";

const _BOLD_RX =
  /(\bNPS Geral\b|\bCSAT Geral\b|\bCES Geral\b|\bJornada CX\b|\bNPS\b|\bCSAT\b|\bCES\b|\bCX\b|[+-]?\d+(?:[.,]\d+)?\s*pp\b|\d+(?:[.,]\d+)?%)/gi;

// Driver name abbreviations — must not trigger sentence breaks
const _ABBREV_RX = /\b(?:Agend|Atend|Prom|Ext|Op)\.\s+/g;

function _applyBold(str, kr) {
  const parts = [];
  let last = 0;
  let m;
  _BOLD_RX.lastIndex = 0;
  while ((m = _BOLD_RX.exec(str)) !== null) {
    if (m.index > last) parts.push(str.slice(last, m.index));
    parts.push(<strong key={kr.k++}>{m[0]}</strong>);
    last = _BOLD_RX.lastIndex;
  }
  if (last < str.length) parts.push(str.slice(last));
  return parts;
}

function formatText(text) {
  if (!text) return text;
  const out = [];
  const kr = { k: 0 };

  // Split into abbreviation-protected segments and normal segments
  const segs = [];
  let lastIdx = 0;
  _ABBREV_RX.lastIndex = 0;
  let am;
  while ((am = _ABBREV_RX.exec(text)) !== null) {
    if (am.index > lastIdx)
      segs.push({ prot: false, t: text.slice(lastIdx, am.index) });
    segs.push({ prot: true, t: am[0] });
    lastIdx = _ABBREV_RX.lastIndex;
  }
  if (lastIdx < text.length) segs.push({ prot: false, t: text.slice(lastIdx) });

  segs.forEach((seg) => {
    if (seg.prot) {
      out.push(..._applyBold(seg.t, kr));
    } else {
      seg.t.split(/(\.\s+(?=[A-ZÁÀÂÃÉÊÍÓÔÕÚÇ])|\.$)/).forEach((s) => {
        if (!s) return;
        if (/^\.\s*$/.test(s)) {
          out.push(".", <br key={kr.k++} />);
        } else {
          out.push(..._applyBold(s, kr));
        }
      });
    }
  });

  return out;
}

export default function EvaInsights({ insights }) {
  const { C, JCFG } = S;

  const [open, setOpen] = useState(0);
  const tc = {
    overview: { bg: "#F0F9FF", bc: "#0369a1", bl: "Visão Geral", Icon: Brain },
    alerta: { bg: C.redBg, bc: C.red, bl: "Alerta", Icon: CircleAlert },
    oportunidade: { bg: C.greenBg, bc: C.green, bl: "Oportunidade", Icon: Zap },
    padrao: { bg: C.blueBg, bc: C.blue, bl: "Padrão", Icon: ScanSearch },
    predicao: { bg: C.purpleBg, bc: C.purple, bl: "Predição", Icon: Brain },
  };

  return (
    <div className="compEvaInsights">
      <div className="eva-header">
        <div className="eva-avatar">
          <Sparkles size={16} color="#fff" />
        </div>
        <div>
          <div className="eva-title">Eva — Insights Proativos</div>
          <div className="eva-subtitle">
            {insights.length} insights gerados automaticamente
          </div>
        </div>
        <div className="eva-live">
          <div className="eva-live-dot" />
          <span className="eva-live-label">Ao vivo</span>
        </div>
      </div>
      {insights.map((ins, i) => {
        let cfg = null;

        Object.keys(tc).map((e) => {
          if (ins.tipo.toLowerCase() === tc[e].bl.toLowerCase()) {
            cfg = tc[e];
          }
        });

        if (!cfg) {
          cfg = tc.padrao;
        }

        const isO = open === i;
        const { Icon } = cfg;
        const borderColor = `${cfg.bc}${ins.tipo === "overview" ? "40" : "20"}`;
        return (
          <div
            key={i}
            className={`insight-card${ins.tipo === "overview" ? " is-overview" : ""}`}
            style={{
              "--_ins-bg": cfg.bg,
              "--_ins-border": borderColor,
              "--_ins-shadow": ins.tipo === "overview" ? `${cfg.bc}15` : "transparent",
            }}
          >
            <div
              className="insight-trigger"
              onClick={() => setOpen(isO ? null : i)}
            >
              <div
                className="insight-icon"
                style={{ "--_icon-bg": cfg.bc + "18" }}
              >
                <Icon size={14} color={cfg.bc} strokeWidth={2} />
              </div>
              <div className="insight-body">
                <div className="insight-meta">
                  <Tag label={cfg.bl} color={cfg.bc} bg={cfg.bc + "18"} />
                  {ins.tipo !== "overview" && (
                    <span className="insight-priority">
                      Prioridade {ins.prio}
                    </span>
                  )}
                </div>
                <div className="insight-title">{formatText(ins.titulo)}</div>
              </div>
              {isO ? (
                <ChevronUp size={15} color={C.text4} />
              ) : (
                <ChevronDown size={15} color={C.text4} />
              )}
            </div>
            {isO && (
              <div className="insight-content">
                <p className="insight-corpo">{formatText(ins.corpo)}</p>
                <div className="insight-acao">
                  <span className="insight-acao-emoji">💡</span>
                  <div>
                    <div
                      className="insight-acao-label"
                      style={{ "--_ins-bc": cfg.bc }}
                    >
                      Ação sugerida
                    </div>
                    <div className="insight-acao-text">{formatText(ins.acao)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
